import React, { useState } from 'react'
import { FunctionComponent, memo, useCallback } from 'react'
import { Formik } from 'formik'
import { useSelector } from 'react-redux'
import { format } from 'date-fns'

import Modal from '../../components/Modal'
import Input from '../../components/Input'
import Button from '../../components/Button'
import {
  LOCATION_TYPE_PLURAL_LABELS,
  LocationTypes,
} from '../../types/location'
import { IAccount, IBranch } from '../../types/user'
import useThunkDispatch from '../../common/useThunkDispatch'
import {
  updateAccountBranch,
  requestBranchDelete,
  rotateSanaPassword,
  setSanaPasswordManual,
  impersonateUser,
} from '../accountsActions'
import Toggle from '../../components/Toggle'
import { currentUserSelector } from '../../auth/authSelectors'
import SyncFailedIcon from '../../icons/sync-failed.svg'
import SyncedIcon from '../../icons/synced.svg'
import CheckThinIcon from '../../icons/check-thin.svg'
import PeopleIcon from '../../icons/people.svg'
import { sanaStatus, SANA_STATUS_LABEL, SANA_STATUS_COLOR } from './sanaStatus'

interface IOwnProps {
  account?: IAccount
  branch: IBranch
  onClose(changed?: any): void
}

const AccountBranchEdit: FunctionComponent<IOwnProps> = ({
  branch,
  account,
  onClose,
}) => {
  const dispatch = useThunkDispatch()
  const currentUser = useSelector(currentUserSelector)
  const isInternal = currentUser?.role === 'internal'
  const [loadingDelete, setLoadingDelete] = useState(false)
  const [loadingRotate, setLoadingRotate] = useState(false)
  const [loadingImpersonate, setLoadingImpersonate] = useState(false)
  const [showSetPasswordModal, setShowSetPasswordModal] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [loadingSetPassword, setLoadingSetPassword] = useState(false)

  const manager = branch.sanaLoginManager
  const status = sanaStatus(manager)
  const [localSyncError, setLocalSyncError] = useState<string | null>(
    manager?.sanaPasswordSyncError || null,
  )
  const [localSyncedAt, setLocalSyncedAt] = useState<string | null>(
    manager?.sanaPasswordSyncedAt || null,
  )

  const deleteBranch = useCallback(async () => {
    if (
      window.confirm(
        // tslint:disable-next-line: max-line-length
        `You are about to delete the branch ${branch.name} with all underlying data. You will not be able to restore it. Are you sure you what to proceed?`,
      )
    ) {
      try {
        setLoadingDelete(true)
        await dispatch(requestBranchDelete(branch.id))
        window.location.reload()
      } catch (e) {
        alert(e)
      }
      setLoadingDelete(false)
    }
  }, [branch.id])

  const handleRotate = async () => {
    if (!manager) return
    setLoadingRotate(true)
    try {
      const result = await dispatch(rotateSanaPassword(manager.id))
      setLocalSyncError(result?.sanaPasswordSyncError || null)
      setLocalSyncedAt(result?.sanaPasswordSyncedAt || null)
    } catch (e) {
      alert(e)
    } finally {
      setLoadingRotate(false)
    }
  }

  const handleSetPasswordManual = async () => {
    if (!manager || !newPassword) return
    setLoadingSetPassword(true)
    try {
      const error = await dispatch(
        setSanaPasswordManual(manager.id, newPassword),
      )
      if (error) alert(error)
      else setShowSetPasswordModal(false)
    } catch (e) {
      alert(e)
    } finally {
      setLoadingSetPassword(false)
    }
  }

  const handleImpersonate = async () => {
    if (!manager) return
    setLoadingImpersonate(true)
    try {
      await dispatch(impersonateUser(manager.id))
    } catch (e) {
      alert(e)
    } finally {
      setLoadingImpersonate(false)
    }
  }

  const onSubmit = async (attrs: IBranch, actions: any) => {
    if (
      attrs.id &&
      (attrs.showTechPrice !== branch.showTechPrice ||
        attrs.navId !== branch.navId ||
        attrs.name !== branch.name ||
        attrs.invoiceMappingRow !== branch.invoiceMappingRow ||
        attrs.address !== branch.address ||
        attrs.address2 !== branch.address2 ||
        attrs.city !== branch.city ||
        attrs.zip !== branch.zip ||
        attrs.state !== branch.state ||
        attrs.shipToCode !== branch.shipToCode ||
        attrs.replenishmentFile !== branch.replenishmentFile ||
        attrs.shipmentApproveFlow !== branch.shipmentApproveFlow ||
        attrs.truckInventoryFeature !== branch.truckInventoryFeature ||
        attrs.binInventoryFeature !== branch.binInventoryFeature)
    ) {
      actions.setSubmitting(true)
      try {
        const updateAttrs: any = {
          id: attrs.id,
          name: attrs.name,
          navId: attrs.navId,
          address: attrs.address,
          address2: attrs.address2,
          city: attrs.city,
          zip: attrs.zip,
          state: attrs.state,
          showTechPrice: attrs.showTechPrice,
          invoiceMappingRow: attrs.invoiceMappingRow,
          shipmentApproveFlow: attrs.shipmentApproveFlow,
          truckInventoryFeature: attrs.truckInventoryFeature,
          binInventoryFeature: attrs.binInventoryFeature,
          replenishmentFile: attrs.replenishmentFile || null,
          shipToCode: attrs.shipToCode,
        }

        const errors = await dispatch(updateAccountBranch(updateAttrs))
        if (!errors) onClose(true)
        else {
          actions.setErrors(errors)
        }
      } catch (e) {
        alert(e && e.message ? e.message : 'Server Error')
      }
      actions.setSubmitting(false)
    } else {
      actions.setSubmitting(false)
    }
  }

  const statusIconProps = {
    title: SANA_STATUS_LABEL[status],
    style: {
      position: 'absolute' as const,
      right: 8,
      top: '50%',
      transform: 'translateY(-5%)',
    },
  }

  const StatusIcon = () => {
    if (status === 'failed') return <SyncFailedIcon {...statusIconProps} />
    if (status === 'synced') return <SyncedIcon {...statusIconProps} />
    return <CheckThinIcon {...statusIconProps} />
  }

  return (
    <Modal onClose={onClose} title="Branch Info">
      <Formik
        initialValues={branch}
        onSubmit={onSubmit}
        render={({
          values,
          errors,
          handleChange,
          handleSubmit,
          isSubmitting,
          dirty,
          setFieldValue,
        }) => (
          <form className="form" onSubmit={handleSubmit}>
            <div className="form_group">
              <Input
                onChange={handleChange('name')}
                name="name"
                label="Name"
                value={values.name}
              />
            </div>
            <div className="form_group">
              <Input
                onChange={handleChange('navId')}
                name="nav_id"
                label="NAV ID"
                value={values.navId}
              />
            </div>
            <div className="form_group">
              <Input
                name="address"
                label="Address Line 1"
                onChange={handleChange('address')}
                value={values.address}
              />
            </div>
            <div className="form_group">
              <Input
                name="address2"
                label="Address Line 2"
                onChange={handleChange('address2')}
                value={values.address2}
              />
            </div>
            <div className="form_row">
              <div className="form_group form_col">
                <Input
                  name="city"
                  label="City"
                  onChange={handleChange('city')}
                  value={values.city}
                />
              </div>
              <div className="form_group form_col">
                <Input
                  name="state"
                  label="State"
                  onChange={handleChange('state')}
                  value={values.state}
                />
              </div>
              <div className="form_group form_col">
                <Input
                  name="zip"
                  label="ZIP"
                  onChange={handleChange('zip')}
                  value={values.zip}
                />
              </div>
            </div>
            <div className="form_group">
              <Input
                onChange={handleChange('shipToCode')}
                name="shipToCode"
                label="Ship To Code"
                defaultValue={values.shipToCode}
              />
            </div>
            <div className="form_group">
              <Input
                onChange={handleChange('invoiceMappingRow')}
                name="invoiceMappingRow"
                label="Invoices mapping"
                defaultValue={values.invoiceMappingRow}
              />
            </div>

            <div className="form_group" style={{ position: 'relative' }}>
              <Input
                name="sanaLoginManager"
                label="System manager"
                defaultValue={
                  values.sanaLoginManager
                    ? `${values.sanaLoginManager.name} (${values.sanaLoginManager.email})`
                    : ''
                }
                placeholder="Not specified"
                disabled
              />
              {manager && <StatusIcon />}
            </div>

            {localSyncError && (
              <div
                style={{
                  backgroundColor: `${SANA_STATUS_COLOR.failed}1A`,
                  color: SANA_STATUS_COLOR.failed,
                  padding: '8px 12px',
                  borderRadius: 4,
                  marginBottom: 8,
                }}
              >
                <div style={{ fontWeight: 'bold' }}>
                  Sana sync paused for this branch
                </div>
                <div>
                  {localSyncError}
                  {localSyncedAt && (
                    <span>
                      , {format(new Date(localSyncedAt), 'MM/DD/YYYY hh:mma')}
                    </span>
                  )}
                </div>
              </div>
            )}

            {manager && (
              <div
                className="form_group"
                style={{ display: 'flex', justifyContent: 'space-between' }}
              >
                <Button
                  onClick={handleRotate}
                  loading={loadingRotate}
                  disabled={loadingRotate}
                  color="primary-link"
                  border
                >
                  ROTATE SANA PASSWORD
                </Button>
                {isInternal && (
                  <>
                    <Button
                      onClick={() => setShowSetPasswordModal(true)}
                      color="primary-link"
                      border
                    >
                      SET PASSWORD MANUALLY
                    </Button>
                    <Button
                      onClick={handleImpersonate}
                      loading={loadingImpersonate}
                      disabled={loadingImpersonate}
                      color="primary-link"
                      border
                    >
                      <PeopleIcon
                        style={{
                          width: 14,
                          height: 14,
                          marginRight: 6,
                          flexShrink: 0,
                        }}
                      />
                      IMPERSONATE
                    </Button>
                  </>
                )}
              </div>
            )}

            <div className="form_group">
              <Input
                onChange={handleChange('replenishmentFile')}
                name="replenishmentFile"
                label="Replenishment file"
                value={values.replenishmentFile || ''}
              />
            </div>

            <div className="form_group form_toggle">
              <Toggle
                value={values.showTechPrice}
                onChange={() =>
                  handleChange('showTechPrice')(!values.showTechPrice)
                }
                option={{
                  value: true,
                  label: 'Show pricing for technicians',
                }}
              />
            </div>

            <div className="form_group form_toggle">
              <Toggle
                value={values.shipmentApproveFlow}
                onChange={() =>
                  handleChange('shipmentApproveFlow')(
                    !values.shipmentApproveFlow,
                  )
                }
                option={{
                  value: true,
                  label: 'Manual Shipment Approval',
                }}
              />
            </div>

            {account?.truckInventoryFeature && (
              <div className="form_group form_toggle">
                <Toggle
                  value={values.truckInventoryFeature || false}
                  onChange={() =>
                    setFieldValue(
                      'truckInventoryFeature',
                      !values.truckInventoryFeature,
                    )
                  }
                  option={{
                    value: true,
                    label: LOCATION_TYPE_PLURAL_LABELS[LocationTypes.TRUCK],
                  }}
                />
              </div>
            )}

            {account?.binInventoryFeature && (
              <div className="form_group form_toggle">
                <Toggle
                  value={values.binInventoryFeature || false}
                  onChange={() =>
                    setFieldValue(
                      'binInventoryFeature',
                      !values.binInventoryFeature,
                    )
                  }
                  option={{
                    value: true,
                    label: LOCATION_TYPE_PLURAL_LABELS[LocationTypes.BIN],
                  }}
                />
              </div>
            )}

            <div className="form_controls">
              <div className="form_controls-container">
                <Button
                  onClick={deleteBranch}
                  color="red-link"
                  loading={loadingDelete}
                  className="form_control -left"
                >
                  Delete
                </Button>
                <Button
                  disabled={isSubmitting}
                  onClick={onClose}
                  loading={loadingDelete}
                  color="default-link"
                  className="form_control -size -right"
                >
                  Cancel
                </Button>
                <Button
                  loading={isSubmitting || loadingDelete}
                  disabled={isSubmitting || !dirty}
                  type="submit"
                  color="success"
                  className="form_control -size"
                >
                  Save
                </Button>
              </div>
            </div>
          </form>
        )}
      />

      {showSetPasswordModal && (
        <Modal
          onClose={() => setShowSetPasswordModal(false)}
          title="Set Sana Password"
        >
          <div className="form">
            <div className="form_group">
              <Input
                name="newPassword"
                label="New Password"
                value={newPassword}
                onChange={(e: any) => setNewPassword(e.target.value)}
              />
            </div>
            <div className="form_controls">
              <div className="form_controls-container">
                <Button
                  onClick={() => setShowSetPasswordModal(false)}
                  color="default-link"
                  className="form_control -right"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSetPasswordManual}
                  loading={loadingSetPassword}
                  disabled={loadingSetPassword || !newPassword}
                  color="success"
                  className="form_control -size"
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </Modal>
  )
}

export default memo(AccountBranchEdit)
